import CopyToClipboard from "@/components/copy-to-clipboard.tsx";
import { useGlobals } from "@/components/providers/globals-provider.tsx";
import Button from "@/components/ui/button";
import Icon from "@/components/ui/icon";
import DynamicSpecSheet from "@/features/downloads/components/dynamic-spec-sheet.tsx";
import { DYNAMIC_SPEC_SHEET } from "@/features/downloads/resources/constants.ts";
import IncludedInReference from "@/features/sub-families/components/summary/included-in-reference.tsx";
import useModelCode from "@/features/sub-families/hooks/use-model-code.ts";
import useSmartnumberFavorites from "@/features/sub-families/hooks/use-smartnumber-favorites.ts";
import useTracking from "@/features/tracking/hooks/use-tracking.ts";
import { useTranslations } from "@/features/translations/components/translations";
import type { Nullable } from "@/types";
import { WebComponentMode } from "@/types";
import ReferenceTooltip from "../reference-tooltip";
import ShortCode from "../short-code";

export type Props = { smartNumber: Nullable<string> };

export default function YourProductCode({ smartNumber }: Props) {
    const { mode } = useGlobals();

    const t = useTranslations();
    const trackEvent = useTracking();
    const modelCode = useModelCode();

    const { onWishlist, isPending, canUseWishlist, toggleWishlist } = useSmartnumberFavorites();

    if (!smartNumber) return null;

    const trackDynamicSpecSheet = () => {
        trackEvent("3d-technical", { label: DYNAMIC_SPEC_SHEET, component: "summary", model_code: modelCode ?? "" });
    };

    const showPinlistAction = mode === WebComponentMode.Website && canUseWishlist;

    const trackCopy = () => {
        trackEvent("3d-copy-to-clipboard", { smartnumber: smartNumber, component: "summary" });
    };

    return (
        <div className="your-product-code">
            <div className="your-product-code__header">
                <div className="your-product-code__header__details">
                    <div className="your-product-code__title">
                        {t("your_product_code")}
                        <ReferenceTooltip label={t("your_product_code")} tooltip={t("your_product_code_tooltip")} />
                    </div>

                    <div className="your-product-code__smart-number">
                        <span>{smartNumber}</span>
                        <CopyToClipboard variant="link" data={smartNumber} onClick={trackCopy}>
                            <Icon name="copy-file" />
                        </CopyToClipboard>
                    </div>

                    <ShortCode smartNumber={smartNumber} />
                </div>
            </div>

            <IncludedInReference />

            <div className="your-product-code__actions">
                <div>
                    <DynamicSpecSheet calculatedSmartNumber={smartNumber} onClick={trackDynamicSpecSheet}>
                        {t("download_spec_sheet")}
                    </DynamicSpecSheet>
                </div>

                {showPinlistAction && (
                    <div className="your-product-code__pin-action">
                        <Button
                            variant="link"
                            className="your-product-code__pin"
                            disabled={isPending}
                            onClick={() => void toggleWishlist()}
                        >
                            <Icon name="push-pin" className="your-product-code__pin-icon--mobile" />
                            <span>{onWishlist ? t("remove_from_pinlist") : t("add_to_pinlist")}</span>
                            <Icon name="push-pin" className="your-product-code__pin-icon--desktop" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
